"use client"

import { useState, useEffect } from "react"
import { signInWithEmailAndPassword } from "firebase/auth"
import { auth, db } from "@/lib/firebase"
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"
import AdminDashboard from "@/components/admin/dashboard"
import { ShieldAlert, Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth()
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  // Login Form State
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isPromoting, setIsPromoting] = useState(false)

  const handleSelfPromote = async () => {
     if (!user) return
     setIsPromoting(true)
     try {
        await setDoc(doc(db, "admins", user.uid), {
           uid: user.uid,
           email: user.email,
           registeredAt: serverTimestamp(),
           isEmergencyPromotion: true
        })
        setIsAdmin(true)
        toast.success("Identity elevated to Administrative status.")
     } catch (e) {
        console.error(e)
        toast.error("Promotion failed. Verify Firestore rules.")
     } finally {
        setIsPromoting(false)
     }
  }

  useEffect(() => {
    const checkAdminStatus = async () => {
      // If we are still loading the auth state, don't change anything
      if (authLoading) return;

      if (!user) {
        setIsAdmin(false)
        return
      }

      // Reset to null whenever user changes to ensure we show the loader during verification
      setIsAdmin(null)

      if (user.role === "admin" || user.role === "superadmin") {
        setIsAdmin(true)
        return
      }

      try {
        const adminDoc = await getDoc(doc(db, "admins", user.uid))
        setIsAdmin(adminDoc.exists())
      } catch (e) {
        console.error("Admin verification failed:", e)
        setIsAdmin(false)
      }
    }
    checkAdminStatus()
  }, [user, authLoading])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoggingIn(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      setError("Invalid credentials or unauthorized access.")
    } finally {
      setIsLoggingIn(false)
    }
  }

  if (authLoading || (user && isAdmin === null)) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-[#b48c36] rounded-full animate-spin"></div>
      </div>
    )
  }

  if (user && isAdmin === true) {
    return <AdminDashboard />
  }

  if (user && isAdmin === false) {
     return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
           <div className="w-full max-w-md bg-white border border-slate-200 p-12 shadow-2xl">
              <ShieldAlert className="mx-auto text-rose-500 mb-6" size={48} />
              <h2 className="text-2xl font-serif font-bold text-slate-950 mb-4">Access Denied</h2>
              <p className="text-slate-500 text-sm mb-8">Your account does not possess the administrative credentials required for this sector.</p>
              
              <div className="space-y-3">
                 <button 
                   onClick={handleSelfPromote}
                   disabled={isPromoting}
                   className="w-full bg-[#b48c36] text-white py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-[#9a772e] transition-all flex items-center justify-center gap-2"
                 >
                    {isPromoting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Register Current Identity as Admin"}
                 </button>

                 <button 
                   onClick={() => auth.signOut()}
                   className="w-full border border-slate-200 text-slate-400 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-slate-50 transition-all"
                 >
                    Return to Public Portal
                 </button>
              </div>
           </div>
        </div>
     )
  }

  // If not authenticated, show the strict Admin Login Portal
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 p-8 sm:p-12 shadow-2xl">
        <div className="flex flex-col items-center text-center mb-10 space-y-4">
          <div className="w-28 h-28 flex items-center justify-center overflow-hidden">
             <img src="/logo-final.png" alt="Rohith's Academy" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-[#b48c36] uppercase tracking-[0.4em] mb-2">Restricted Area</p>
            <h1 className="text-3xl font-serif font-medium text-slate-950 leading-tight">
              Administrative <br /> Verification
            </h1>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 text-xs font-bold uppercase tracking-widest text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block text-center">Admin Email</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 p-4 text-center text-sm focus:outline-none focus:border-[#b48c36] transition-colors"
              required
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block text-center">Security Passphrase</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 p-4 text-center text-sm focus:outline-none focus:border-[#b48c36] transition-colors"
              required
            />
          </div>

          <button 
            type="submit" 
            disabled={isLoggingIn}
            className="w-full bg-slate-950 text-white p-4 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors flex items-center justify-center disabled:opacity-50"
          >
            {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Authenticate Identity"}
          </button>
        </form>
      </div>
    </div>
  )
}
